import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import styles from './DuplicateForDigitalModal.module.scss';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

export type ProductType = 'Audio' | 'Music Video' | 'A/V Bundle';

type Props = {
  onChange?: (value: ProductType) => void;
};

const PRODUCT_CONFIG: Record<
  ProductType,
  { iconClasses: string[]; translationKey: string }
> = {
  Audio: {
    iconClasses: ['fas fa-music'],
    translationKey: 'common.digitalProductTypeAudio',
  },
  'Music Video': {
    iconClasses: ['fas fa-film'],
    translationKey: 'common.digitalProductTypeMusicVideo',
  },
  'A/V Bundle': {
    iconClasses: ['fas fa-music', 'fas fa-film'],
    translationKey: 'common.digitalProductTypeAvBundle',
  },
};

const PRODUCT_TYPES = Object.keys(PRODUCT_CONFIG) as ProductType[];

const BUTTON_WIDTH = 145;

export const ProductTypeSelector: React.FC<Props> = ({ onChange }) => {
  const [selected, setSelected] = useState<ProductType>('Audio');
  const { t } = useTranslation();

  const selectedIndex = useMemo(
    () => Math.max(0, PRODUCT_TYPES.indexOf(selected)),
    [selected]
  );

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [highlightLeft, setHighlightLeft] = useState(0);

  const updateHighlightPosition = () => {
    const container = containerRef.current;
    if (!container) return;

    const containerWidth = container.getBoundingClientRect().width;
    const count = PRODUCT_TYPES.length;

    const totalButtonsWidth = count * BUTTON_WIDTH;
    const remainingSpace = containerWidth - totalButtonsWidth;

    const gap = count > 1 ? Math.max(0, remainingSpace / (count - 1)) : 0;

    const left = selectedIndex * (BUTTON_WIDTH + gap);
    setHighlightLeft(left);
  };

  useLayoutEffect(() => {
    updateHighlightPosition();
    window.addEventListener('resize', updateHighlightPosition);
    return () => window.removeEventListener('resize', updateHighlightPosition);
  }, [selectedIndex]);

  const handleSelect = (type: ProductType) => {
    setSelected(type);
    onChange?.(type);
  };

  return (
    <div className={styles.wrapper}>
      <div
        ref={containerRef}
        className={styles.productBtnGroup}
        role="radiogroup"
      >
        {PRODUCT_TYPES.map((type) => {
          const { translationKey, iconClasses } = PRODUCT_CONFIG[type];
          const isActive = selected === type;

          return (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={isActive}
              onClick={() => handleSelect(type)}
              className={clsx(styles.button, isActive && styles.active)}
            >
              <div className={styles.iconRow}>
                {iconClasses.map((iconClass) => (
                  <i
                    key={iconClass}
                    className={clsx(
                      iconClass,
                      styles.icon,
                      isActive && styles.iconActive
                    )}
                    aria-hidden="true"
                  />
                ))}
              </div>

              <span
                className={clsx(styles.label, isActive && styles.labelActive)}
              >
                {t(translationKey)}
              </span>
            </button>
          );
        })}
      </div>

      {/* Footer Bar */}
      <div className={styles.footerContainer}>
        <div
          className={styles.footerHighlight}
          style={{ width: BUTTON_WIDTH, marginLeft: highlightLeft }}
        />
      </div>
    </div>
  );
};
